import { getParentSubscriptionReports, getParentSubscriptionStats } from '@/app/services/reports/reports';
import * as Actions from '@/app/store/actions';
import { Avatar } from '@material-ui/core';
import moment from 'moment-timezone';
import Pusher from 'pusher-js';
import qs from 'qs';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import secureLocalStorage from 'react-secure-storage';
import { useReactToPrint } from 'react-to-print';
import AppliedFiltersBar from '@shared/Reports/AppliedFiltersBar';
import ReportsHeader from '@shared/Reports/ReportsHeader';
import TableTooltip from '@shared/Reports/TableTooltip';
import SubscriptionFiltersBar from './SubscriptionFiltersBar';
import SubscriptionPrintViewTable from './SubscriptionPrintViewTable';
import SubscriptionTable from './SubscriptionTable';
import StatBox from '@shared/Reports/StatBox';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

/* Search Types 
  active - shows active users in the selected date range; this is the default search if none is specified
  renews - shows users with a "Renews On" on date in the selected range
  since - shows users with a "Subscribed Since" date in the selected range
*/
const SubscriptionReport = () => {
	const [filters, setFilters] = useState({
		page: 1,
		room_id: [],
		start_date: null,
		end_date: null,
		package_type: null,
		search_type: null,
		status: null,
		timezone: null,
		export: null,
		sort: 'student',
		dir: 'desc',
		parent_id: [],
	});
	const user = useSelector((state) => state.auth.user);
	const viewAsId = secureLocalStorage.getItem('view_as_id');
	const authToken = secureLocalStorage.getItem('jwt_access_token');
	const [loading, setLoading] = useState(false);
	const [printData, setPrintData] = useState([]);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [printDataLoading, setPrintDataLoading] = useState(false);
	const printRef = useRef();
	const [rows, setRows] = useState([]);
	const [totalResults, setTotalResults] = useState(0);
	const dispatch = useDispatch();
	const cache = useRef({}); // Cache object to prevent refetching pages already fetched
	const prevFilters = useRef(filters); // Track previous filters to know when to refetch a set of results
	const [statsData, setStatsData] = useState({});
	const [exportType, setExportType] = useState(null);
	const [exportLoading, setExportLoading] = useState(false);
	const [statsLoading, setStatsLoading] = useState(true);
	const [hasFilterApplied, setHasFilterApplied] = useState(false);
	const exportTimeoutRef = useRef(null);
	const navigate = useNavigate();

	useEffect(() => {
		const pusher = new Pusher(import.meta.env.VITE_PUSHER_CHANNEL_ID, {
			cluster: import.meta.env.VITE_PUSHER_CLUSTER_ID,
			channelAuthorization: {
				headers: {
					Authorization: `Bearer ${authToken}`,
				},
				endpoint: `${import.meta.env.VITE_API_ENDPOINT}broadcasting/auth`,
			},
		});
		const channel = pusher.subscribe(
			`private-App.Models.User.${user.role[0] === 'super_school_admin' ? viewAsId : user.data?.id}`
		);

		channel.bind('report.export.complete', (res) => {
			const exportLink = JSON.parse(window.localStorage.getItem('export_uuid'));
			if (exportLink && exportLink === res.uuid) {
				setExportLoading(false);
				if (exportTimeoutRef.current) {
					clearTimeout(exportTimeoutRef.current);
				}
				window.localStorage.removeItem('export_uuid');

				const anchor = document.createElement('a');
				anchor.href = res.link;
				anchor.download = res.filename;
				anchor.click();
				anchor.remove();
			}
		});

		return () => {
			pusher.disconnect();
		};
	}, []);

	const handlePrint = useReactToPrint({
		content: () => printRef.current,
	});

	useEffect(() => {
		setFilters({
			...filters,
			timezone: user.data.school.timezone,
		});
	}, [user]);

	const calculatePercentChange = (current, previous) => {
		if (previous === 0) {
			return current * 100;
		}
		return (((current - previous) / previous) * 100).toFixed(1);
	};

	const generateQueryParams = (filters, exportType = null) => {
		return {
			page: filters.page,
			start_date: filters.start_date,
			end_date: filters.end_date,
			search_type: filters.search_type?.value, // active, renewals, since
			timezone: filters.timezone,
			package_type: filters.package_type?.value,
			export: exportType,
			parent_id: filters.parent_id?.length > 0 ? filters.parent_id.map((item) => item.value) : null,
			sort: filters.sort,
			dir: filters.dir,
			room_id: filters.room_id?.length > 0 ? filters.room_id.map((item) => item.value) : null,
		};
	};

	// Helper function to update filters and reset page to 1 if any filter other than page changes
	const updateFilters = (newFilters) => {
		const filtersChanged = Object.keys(newFilters).some((key) => key !== 'page' && newFilters[key] !== filters[key]);
		if (filtersChanged) {
			setFilters({ ...newFilters, page: 1 });
		} else {
			setFilters(newFilters);
		}
	};
	// checks if any actionable filters have changed
	const hasAtLeastOneFilter = (filters) => {
		return Object.keys(filters).some(
			(key) =>
				key !== 'timezone' &&
				key !== 'page' &&
				key !== 'sort' &&
				key !== 'dir' &&
				(Array.isArray(filters[key]) ? filters[key].length > 0 : filters[key])
		);
	};

	useEffect(() => {
		// Clear cache if any filter other than page changes
		const filtersChanged = Object.keys(filters).some(
			(key) => key !== 'page' && filters[key] !== prevFilters.current[key]
		);
		//  create a list of the filters than changed
		const changedFilters = Object.keys(filters).filter((key) => filters[key] !== prevFilters.current[key]);

		// clearing the date should update the results
		const dateCleared = changedFilters.includes('start_date') && changedFilters.includes('end_date');

		if (!dateCleared) {
			// if a date changed, but the other one is not set, we don't need to do anything until the other one is set
			if (changedFilters[0] === 'start_date' && !filters.end_date) {
				return;
			}
			if (changedFilters[0] === 'end_date' && !filters.start_date) {
				return;
			}
		}

		if (filtersChanged) {
			cache.current = {};
		}
		prevFilters.current = filters;

		const filterApplied = hasAtLeastOneFilter(filters);
		// update the state in here so the we can pass the most current hasFilterApplied state to the table
		setHasFilterApplied(filterApplied);

		if (filterApplied) {
			setLoading(true);
			const queryParams = generateQueryParams(filters);
			const queryKey = JSON.stringify(queryParams);

			// Check if the results for the current page are already in the cache
			if (cache.current[queryKey]) {
				const cachedData = cache.current[queryKey];
				setRows(cachedData.data);
				setTotalResults(cachedData.total);
				setLoading(false);

				// Fetch stats data only when filters other than page change, total results > 0, start_date and end_date are set, and no parent_id is selected
				if (
					filtersChanged &&
					cachedData.total > 0 &&
					filters.start_date &&
					filters.end_date &&
					filters.parent_id.length === 0
				) {
					fetchStatsData(queryParams);
				} else if (filtersChanged) {
					setStatsData({});
				}
			} else {
				getParentSubscriptionReports(queryParams)
					.then((res) => {
						setRows(res.data.data);
						setTotalResults(res.data.meta.total);
						// Store the results in the cache
						cache.current[queryKey] = {
							data: res.data.data,
							total: res.data.meta.total,
						};

						// Fetch stats data only when filters other than page change, total results > 0, start_date and end_date are set, and no parent_id is selected
						if (
							filtersChanged &&
							res.data.meta.total > 0 &&
							filters.start_date &&
							filters.end_date &&
							filters.parent_id.length === 0
						) {
							fetchStatsData(queryParams);
						} else if (filtersChanged) {
							setStatsData({});
						}
					})
					.catch(() => {
						dispatch(
							Actions.showMessage({
								message: 'Failed to fetch report',
								variant: 'error',
							})
						);
						setRows([]);
						setTotalResults(0);
					})
					.finally(() => {
						setLoading(false);
					});
			}
		} else {
			// Clear data if no filters are applied
			setRows([]);
			setTotalResults(0);
			setStatsData({});
		}
	}, [filters]);

	// Subscription stats response data for reference
	// start_date - string YYYY-MM-DD
	// end_date - string YYYY-MM-DD
	// timezone - string ex: America/New_York
	// active - int - Number of active subscriptions during the time period
	// beginning - int - Number of active subscriptions at the start of the time period
	// end - int - Number of active subscriptions at the end of the time period
	// during - int - Number of subscriptions acquired during the time period
	// length - number - Average length of subscription in days

	const fetchStatsData = async (queryParams) => {
		setStatsLoading(true);
		try {
			// Fetch current stats
			const res = await getParentSubscriptionStats(queryParams);
			const stats = res.data;

			// Parse queryParams to extract start_date and end_date
			const params = qs.parse(queryParams);
			const startDate = moment(params.start_date);
			const endDate = moment(params.end_date);
			const duration = endDate.diff(startDate, 'days');
			const previousStartDate = startDate.clone().subtract(duration, 'days').format('YYYY-MM-DD');
			const previousEndDate = endDate.clone().subtract(duration, 'days').format('YYYY-MM-DD');

			// Create previous queryParams string
			const previousQueryParams = generateQueryParams({
				...filters,
				start_date: previousStartDate,
				end_date: previousEndDate,
			});

			// Fetch previous stats
			const previousRes = await getParentSubscriptionStats(previousQueryParams);
			const previousStats = previousRes.data;

			const formatDateRange = (start, end) => {
				const startDate = moment(start);
				const endDate = moment(end);
				if (startDate.month() === endDate.month()) {
					return `${startDate.format('MMM D')} - ${endDate.format('D')}`;
				} else {
					return `${startDate.format('MMM D')} - ${endDate.format('MMM D')}`;
				}
			};

			const formatDuration = (days) => {
				if (days === 0) {
					return '0 days';
				}

				const years = Math.floor(days / 365);
				const months = Math.floor((days % 365) / 30);
				const weeks = Math.floor(((days % 365) % 30) / 7);
				const remainingDays = Math.round(((days % 365) % 30) % 7);
				let durationString = '';

				const parts = [];

				if (years > 0) parts.push(`${years} year${years > 1 ? 's' : ''}`);
				if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
				if (weeks > 0) parts.push(`${weeks} week${weeks > 1 ? 's' : ''}`);
				if (remainingDays > 0) parts.push(`${remainingDays} day${remainingDays > 1 ? 's' : ''}`);

				durationString = parts.join(', ');

				return durationString;
			};

			setStatsData({
				active: {
					total: stats.active || 0,
					percentChange: calculatePercentChange(stats.active, previousStats.active),
					dateRange: formatDateRange(previousStats.start_date, previousStats.end_date),
				},
				duration: {
					total: formatDuration(stats.length || 0),
					percentChange: calculatePercentChange(stats.length, previousStats.length),
					dateRange: formatDateRange(previousStats.start_date, previousStats.end_date),
				},
				retention: {
					total: stats.retention + '%',
					percentChange: calculatePercentChange(stats.retention, previousStats.retention),
					dateRange: formatDateRange(previousStats.start_date, previousStats.end_date),
				},
				active_families: {
					total: stats.active_families,
					percentChange: null,
				},
			});
		} catch (error) {
			dispatch(
				Actions.showMessage({
					message: 'Failed to fetch statistics data',
					variant: 'error',
				})
			);
			setStatsData({
				active: {
					total: 0,
					percentChange: 0,
					dateRange: '',
				},
				duration: {
					total: '0 days',
					percentChange: 0,
					dateRange: '',
				},
				retention: {
					total: '00:00',
					percentChange: 0,
					dateRange: '',
				},
			});
		}
		setStatsLoading(false);
	};

	const handleExport = async (type) => {
		const queryParams = generateQueryParams(filters, type);
		try {
			setExportLoading(true);
			queryParams.uuid = uuidv4();
			window.localStorage.setItem('export_uuid', JSON.stringify(queryParams.uuid));
			await getParentSubscriptionReports(queryParams);

			// Set a timeout to reset exportLoading if no response within 20 seconds
			exportTimeoutRef.current = setTimeout(() => {
				if (!exportLoading) return;
				setExportLoading(false);
				dispatch(
					Actions.showMessage({
						message: 'Export failed to generate. Please try again.',
						variant: 'error',
					})
				);
			}, 20000);
		} catch (error) {
			dispatch(
				Actions.showMessage({
					message: 'Failed to export report',
					variant: 'error',
				})
			);
		}
	};

	const handlePrintClick = async () => {
		setExportType('print');
		setPrintDataLoading(true);
		setPrintData([]);

		try {
			const queryParams = generateQueryParams(filters, 'print');
			const res = await getParentSubscriptionReports(queryParams);
			setPrintData(res.data.data);
		} catch (error) {
			console.error('Error fetching all pages for print:', error);
		} finally {
			setPrintDataLoading(false);
		}
	};

	useEffect(() => {
		if (!printDataLoading && printData.length > 0) {
			handlePrint();
		}
	}, [printDataLoading, printData]);

	const capitalize = (str) => {
		if (!str) return '';
		return str.charAt(0).toUpperCase() + str.slice(1);
	};

	const columns = [
		{
			id: 'student',
			label: 'Student',
			render: (row) => {
				return (
					<div className="flex items-center gap-16">
						<Avatar className="w-32 h-32" src={row.students[0].photo} />
						<TableTooltip>
							<button
								className="hover:underline cursor-pointer font-bold truncate"
								onClick={() => {
									setFilters({
										...filters,
										page: 1,
										student_id: [
											{
												label: `${capitalize(row.students[0].first_name)} ${capitalize(row.students[0].last_name)}`,
												value: row.students[0].id,
											},
										],
									});
								}}
								type="button"
							>
								{filters.student_name === 'first'
									? `${capitalize(row.students[0].first_name)} ${capitalize(row.students[0].last_name)}`
									: `${capitalize(row.students[0].last_name)}, ${capitalize(row.students[0].first_name)}`}
							</button>
						</TableTooltip>
					</div>
				);
			},
			printRender: (row) => `${capitalize(row.students[0].first_name)} ${capitalize(row.students[0].last_name)}`,
			widthClass: 'min-w-256',
		},

		{
			id: 'room',
			label: 'Room',
			render: (row) => (
				<div className="flex items-center gap-8">
					<Avatar className="w-32 h-32" src={row.students[0].room.photo} />
					<TableTooltip>
						<button
							className="hover:underline cursor-pointer font-bold truncate"
							onClick={() =>
								setFilters({
									...filters,
									page: 1,
									room_id: [{ label: row.students[0].room.name, value: row.students[0].room.id }],
								})
							}
							type="button"
						>
							{row.students[0].room.name}
						</button>
					</TableTooltip>
				</div>
			),
			printRender: (row) => <span className="break-all">{row.students[0].room.name}</span>,
			widthClass: 'min-w-128',
			printWidthClass: 'min-w-128',
		},
		{
			id: 'parent',
			label: 'Subscriber',
			render: (row) => (
				<div className="flex items-center gap-8">
					<Avatar className="w-32 h-32" src={row.thumb} />
					<div className="flex flex-col truncate">
						<TableTooltip content={`${capitalize(row.first_name)} ${capitalize(row.last_name)}`}>
							<span className="hover:underline cursor-pointer font-bold truncate">
								{capitalize(row.first_name)} {capitalize(row.last_name)}
							</span>
						</TableTooltip>

						<span>{capitalize(row.relation_with_child)}</span>
					</div>
				</div>
			),
			printRender: (row) => (
				<span className="break-all">{`${capitalize(row.first_name)} ${capitalize(row.last_name)}`}</span>
			),
			widthClass: 'min-w-200',
		},

		{
			id: 'email',
			label: 'Subscriber Email',

			render: (row) => (
				<TableTooltip title={row.email}>
					<div className="flex items-center gap-16 truncate">
						<p className="hover:underline cursor-pointer truncate">{row.email}</p>
					</div>
				</TableTooltip>
			),
			printRender: (row) => (
				<span className="break-all leading-snug" style={{ lineHeight: '.25' }}>
					{row.email}
				</span>
			),
			widthClass: 'min-w-200',
		},
		{
			id: 'package_type',
			label: 'Plan',
			render: (row) => {
				return (
					<div className="flex items-center gap-16">
						<span className="hover:underline cursor-pointer">{row.package_type}</span>
					</div>
				);
			},
			printRender: (row) => row.package_type,
			widthClass: 'min-w-96',
			align: 'right',
		},
		{
			id: 'subscription_date',
			label: 'Subscribed Since',
			render: (row) => {
				if (!row.created_at) {
					return (
						<div className="flex flex-col">
							<span>Not Subscribed</span>
						</div>
					);
				}

				const date = moment(row.created_at);
				const duration = moment.duration(moment().diff(date));
				const years = duration.years();
				const months = duration.months();
				const days = duration.days();
				let durationString = '';

				if (years > 0) {
					durationString += `${years} year${years > 1 ? 's' : ''}`;
				}
				if (months > 0) {
					if (durationString) durationString += ', ';
					durationString += `${months} month${months > 1 ? 's' : ''}`;
				}
				if (days > 0) {
					if (durationString) durationString += ', ';
					durationString += `${days} day${days > 1 ? 's' : ''}`;
				}

				return (
					<div className="flex flex-col">
						<span>{date.format('MM/DD/YY')}</span>
						<span>{durationString}</span>
					</div>
				);
			},
			printRender: (row) => (
				<span className="text-right">
					{row.created_at ? moment(row.created_at).format('MM/DD/YY') : 'Not Subscribed'}
				</span>
			),
			align: 'right',
			widthClass: '',
		},
		{
			id: 'renewal_date',
			label: 'Renews On',
			render: (row) => {
				const date = moment(row.ending_date);

				return (
					<div className="flex flex-col">
						<span>{row.ending_date ? date.format('MM/DD/YY') : <>&mdash;</>}</span>
					</div>
				);
			},
			printRender: (row) => (
				<span className="text-right">
					{row.ending_date ? moment(row.ending_date).format('MM/DD/YY') : <>&mdash;</>}
				</span>
			),
			align: 'right',
			widthClass: '',
		},
	];

	return (
		<div className="">
			<ReportsHeader
				exportLoading={exportLoading}
				exportsDisabled={!rows?.length}
				onBack={() => navigate(-1)}
				onDownloadPDF={() => handleExport('pdf')}
				onExport={handleExport}
				onPrint={handlePrintClick}
				pdfLoading={false}
				printLoading={printDataLoading}
				subtitle="Track key details of subscribers, including subscription package types and subscription renewal dates."
				title="Subscription Report"
			/>
			<SubscriptionFiltersBar filters={filters} setFilters={updateFilters} />
			<AppliedFiltersBar filters={filters} reportType="subscription" setFilters={updateFilters} />
			<div className="bg-white">
				{Object.keys(statsData).length > 0 ? (
					<div className="px-32 py-48">
						<div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-16">
							<StatBox
								data={statsData.active}
								loading={statsLoading || loading}
								title="Active Subscribers"
								type="totalStudent"
							/>
							<StatBox
								data={statsData.duration}
								loading={statsLoading || loading}
								title="Avg. Duration"
								type="dailyStudent"
							/>
							<StatBox
								data={statsData.retention}
								loading={statsLoading || loading}
								title="Retention Rate"
								type="dailyTime"
							/>
							<StatBox
								data={statsData.active_families}
								loading={statsLoading || loading}
								title="Total Active Families Today"
								type="totalStudent"
								percentChange={null}
							/>
						</div>
					</div>
				) : null}
				<div>
					<SubscriptionTable
						columns={columns}
						data={rows}
						dir={filters.dir}
						filters={filters}
						hasFilterApplied={hasFilterApplied}
						loading={loading}
						page={filters.page}
						rowsPerPage={rowsPerPage}
						setDir={(dir) => updateFilters({ ...filters, dir })}
						setFilters={updateFilters}
						setPage={(page) => updateFilters({ ...filters, page })}
						setRowsPerPage={setRowsPerPage}
						setSort={(sort) => updateFilters({ ...filters, sort })}
						sort={filters.sort}
						totalResults={totalResults}
					/>
				</div>
				<div className="h-0 overflow-hidden">
					<SubscriptionPrintViewTable
						columns={columns}
						data={printData}
						exportType={exportType}
						filters={filters}
						ref={printRef}
						title="Subscription Report"
						user={user}
					/>
				</div>
			</div>
		</div>
	);
};

export default SubscriptionReport;
